import { Loader2, MessageCircle } from 'lucide-react';
import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react';

import { useClientCommunications } from '../ClientCommunicationsContext';
import { SmsMessageBubble } from '../items/SmsMessageBubble';

export const TextsView: React.FC = () => {
  const { communications, isLoading } = useClientCommunications();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevLoadingRef = useRef(isLoading);
  const shouldAutoScrollRef = useRef(false);

  // Filter to only SMS messages and reverse for chronological order (oldest first)
  const smsMessages = useMemo(() => {
    const filtered = communications.filter((c) => c.type === 'sms');
    // Backend returns newest first, we want oldest first for chat view
    return [...filtered].reverse();
  }, [communications]);

  const prevLengthRef = useRef(smsMessages.length);

  // Auto-scroll to bottom on initial load and when new messages arrive
  useLayoutEffect(() => {
    const wasLoading = prevLoadingRef.current;
    const prevLength = prevLengthRef.current;

    prevLoadingRef.current = isLoading;
    prevLengthRef.current = smsMessages.length;

    if (!messagesEndRef.current) return;

    // Scroll on initial load (loading -> not loading with data)
    if (wasLoading && !isLoading && smsMessages.length > 0) {
      // Enable auto-scroll for ResizeObserver to handle image loading
      shouldAutoScrollRef.current = true;
      // Use requestAnimationFrame to ensure DOM is painted before scrolling
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
      });
      return;
    }

    // Scroll when new messages arrive (length increases while not loading)
    if (!isLoading && smsMessages.length > prevLength) {
      shouldAutoScrollRef.current = true;
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [smsMessages.length, isLoading]);

  // ResizeObserver to handle auto-scroll when images load and expand container
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const resizeObserver = new ResizeObserver(() => {
      // Only auto-scroll if we're supposed to be at the bottom
      if (shouldAutoScrollRef.current && messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'instant' });
      }
    });

    resizeObserver.observe(container);

    // Disable auto-scroll after a delay to allow images to load
    // After this, user can scroll freely without being snapped to bottom
    const timeout = setTimeout(() => {
      shouldAutoScrollRef.current = false;
    }, 2000);

    return () => {
      resizeObserver.disconnect();
      clearTimeout(timeout);
    };
  }, [smsMessages.length]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[200px]">
        <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (smsMessages.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[200px] text-center px-4">
        <div className="w-10 h-10 rounded-full bg-zinc-200 flex items-center justify-center mb-3">
          <MessageCircle className="h-5 w-5 text-zinc-500" />
        </div>
        <p className="text-sm font-semibold text-zinc-800">
          No text messages yet
        </p>
        <p className="text-xs text-zinc-500 font-medium mt-1">
          Send a message to start the conversation
        </p>
      </div>
    );
  }

  // Group consecutive messages from same direction with smart date headers
  // Add time breaks when there's a significant gap (like iMessage sessions)
  type DateGroup = { type: 'date'; date: string };
  type TimeBreak = { type: 'time_break'; timestamp: string };
  type MessageGroup = {
    type: 'messages';
    direction: 'inbound' | 'outbound';
    messages: typeof smsMessages;
  };
  const groupedMessages: Array<DateGroup | TimeBreak | MessageGroup> = [];

  // Time gap threshold for showing a time separator (1 hour in milliseconds)
  const TIME_BREAK_THRESHOLD_MS = 60 * 60 * 1000;

  let lastDate: string | null = null;
  let lastTimestamp: number | null = null;

  smsMessages.forEach((msg) => {
    const msgDate = new Date(msg.timestamp).toDateString();
    const msgTime = new Date(msg.timestamp).getTime();

    // Add date separator if this is a new day
    if (lastDate !== msgDate) {
      groupedMessages.push({ type: 'date', date: msgDate });
      lastDate = msgDate;
      lastTimestamp = null; // Reset time tracking for new day
    }

    // Check for time break within the same day (like iMessage sessions)
    if (lastTimestamp !== null) {
      const timeDiff = msgTime - lastTimestamp;
      if (timeDiff >= TIME_BREAK_THRESHOLD_MS) {
        groupedMessages.push({ type: 'time_break', timestamp: msg.timestamp });
      }
    }

    lastTimestamp = msgTime;

    // Check if we can add to the last message group (same direction, no time break just added)
    const lastGroup = groupedMessages[groupedMessages.length - 1];
    if (
      lastGroup &&
      lastGroup.type === 'messages' &&
      lastGroup.direction === msg.direction
    ) {
      lastGroup.messages.push(msg);
    } else {
      groupedMessages.push({
        type: 'messages',
        direction: msg.direction,
        messages: [msg],
      });
    }
  });

  const formatDateHeader = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    }
    if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTimeBreak = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <div
      ref={containerRef}
      className="flex flex-col gap-2 px-3 py-3 min-h-full"
    >
      {groupedMessages.map((group, groupIndex) => {
        if (group.type === 'date') {
          return (
            <div
              key={`date-${groupIndex}`}
              className="flex items-center justify-center py-2"
            >
              <span className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wide bg-zinc-200 px-2.5 py-0.5 rounded">
                {formatDateHeader(group.date)}
              </span>
            </div>
          );
        }

        if (group.type === 'time_break') {
          return (
            <div
              key={`time-${groupIndex}`}
              className="flex items-center justify-center py-1.5"
            >
              <span className="text-[10px] font-medium text-zinc-400">
                {formatTimeBreak(group.timestamp)}
              </span>
            </div>
          );
        }

        const messageGroup = group;
        return (
          <div
            key={`group-${groupIndex}`}
            className={`flex flex-col gap-0.5 ${
              messageGroup.direction === 'outbound'
                ? 'items-end'
                : 'items-start'
            }`}
          >
            {messageGroup.messages.map((msg, msgIndex) => (
              <SmsMessageBubble
                key={msg.id}
                message={msg}
                showTimestamp={msgIndex === messageGroup.messages.length - 1}
              />
            ))}
          </div>
        );
      })}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default TextsView;
